import React, { useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import {
  CostPriceConversion,
  useGetAudExchangeRates,
  useGetConvertedCostPricesInAud,
} from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';
import { useGetServicesCostPrices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetServicesDeparturesInventory } from '~/hooks/apis/tours/useTourInventory';

import { TourFormDeparture } from '~/services/ToursService';

import { FixedCostPriceItem, ServiceCostPrices, SlidingScalePricingItem, TourService } from '~/types/tours/costPrice';
import { servicePricing } from '~/types/tours/supplier';

import CostBreakdown from './CostBreakdown';
import FixedPriceTable from './FixedPriceTable';
import { SharedInventoryNote } from './OfferSplit';
import SlidingScaleTable from './SlidingScaleTable';
import SlidingScaleUploadDialog from './SlidingScaleUploadDialog';
import SupplierCostSection from './SupplierCostSection';
import TourDepartureSelector from './TourDepartureSelector';
import TourInventorySection from './TourInventorySection';
import TourServiceOptionLabel from './TourServiceOptionLabel';
import { SupplierCostPrices, aggregateCostPricesInAud, getAggregateCostPriceCurrencies } from './aggregateCostPrices';
import { buildScaleAnnotation, fixedPricingAsScale, ownInventory, summariseDepartureCost } from './departureInventory';
import { groupDeparturesForSelector } from './departureRows';
import { inventoryAtDate } from './inventorySnapshot';
import { serviceCostPriceRows } from './serviceCostPriceRows';
import useInventorySnapshot from './useInventorySnapshot';

interface Props {
  tourId: string;
  services: Array<TourService>;
  departures: Array<TourFormDeparture>;
  /** Opens a service's own view, for pricing that can't be uploaded from here. */
  onManageService: (serviceId: string) => void;
}

function pricesForDeparture(
  data: ServiceCostPrices | undefined,
  departureIds: Set<string>,
  season?: string,
): { sliding: Array<SlidingScalePricingItem>; fixed: Array<FixedCostPriceItem> } {
  const rows = serviceCostPriceRows(data);
  const sliding = [
    ...rows.pricing,
    ...rows.byDeparturePricing.filter((item) => departureIds.has(item.departure)),
  ].filter((item) => !season || item.season.toLowerCase() === season.toLowerCase());
  const perGroup = !!data && ('fixedPerGroup' in data || 'fixedPerGroupByDeparture' in data);
  const fixed = perGroup
    ? []
    : [...rows.fixedPrices, ...rows.fixedByDeparturePrices.filter((item) => departureIds.has(item.departure))].filter(
        (item) => !season || item.season.toLowerCase() === season.toLowerCase(),
      );

  return { sliding, fixed };
}

export default function AllServiceCostPrices({ tourId, services, departures, onManageService }: Props) {
  const [selectedDepartureKey, setSelectedDepartureKey] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [uploadServiceId, setUploadServiceId] = useState<string | null>(null);
  // An LTLE's departures are shared with its base tour, whose inventory shows them in full.
  const linkedService = services.find((service) => !!service.linkedTourOfferId);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const results = useGetServicesCostPrices(
    services.map((service) => ({ supplierId: service.supplierId, serviceId: service.id })),
  );
  const departureGroups = useMemo(() => groupDeparturesForSelector(departures), [departures]);
  const visibleDepartureGroups = useMemo(
    () =>
      selectedDepartureKey
        ? departureGroups.filter(([departureKey]) => departureKey === selectedDepartureKey)
        : departureGroups,
    [departureGroups, selectedDepartureKey],
  );
  const selectedDepartures = useMemo(
    () => departureGroups.find(([departureKey]) => departureKey === selectedDepartureKey)?.[1] ?? [],
    [departureGroups, selectedDepartureKey],
  );
  const {
    data: inventory,
    inventoriesByService = {},
    isLoading: isLoadingInventory,
    error: inventoryError,
  } = useGetServicesDeparturesInventory({
    serviceIds: selectedDepartureKey ? services.map((service) => service.id) : [],
    packageDepartureIds: selectedDepartures.flatMap((departure) => departure.packageDepartureId ?? []),
  });
  const { date: snapshotDate, onDateChange } = useInventorySnapshot(inventory);

  const seasonByDeparture = useMemo(() => {
    const seasons = new Map<string, string>();

    results.forEach((result) => {
      const data = result.data;
      const items =
        data && 'slidingScaleByDeparture' in data
          ? data.slidingScaleByDeparture
          : data && 'fixedByDeparture' in data
            ? data.fixedByDeparture
            : data && 'fixedPerGroupByDeparture' in data
              ? data.fixedPerGroupByDeparture
              : [];
      items.forEach((item) => seasons.set(item.departure, item.season));
    });

    return seasons;
  }, [results]);
  const selectedServiceCostSummaries = useMemo(() => {
    if (!selectedDepartureKey || !selectedDepartures.length) {
      return [];
    }

    const departureIds = new Set(selectedDepartures.map((departure) => departure.departureId));
    const season = selectedDepartures.map((departure) => seasonByDeparture.get(departure.departureId)).find(Boolean);

    return services.map((service, index) => {
      const { sliding, fixed } = pricesForDeparture(results[index].data, departureIds, season);
      const serviceInventory = inventoriesByService[service.id];
      const fixedScaleLength = serviceInventory?.entries.filter((entry) => entry.operation === 'allocated').length ?? 1;
      const pricing = sliding.length ? sliding : fixedPricingAsScale(fixed, fixedScaleLength);

      return summariseDepartureCost(
        ownInventory(inventoryAtDate(serviceInventory, snapshotDate), service.tourOfferId),
        pricing,
      );
    });
  }, [
    inventoriesByService,
    results,
    selectedDepartureKey,
    selectedDepartures,
    seasonByDeparture,
    services,
    snapshotDate,
  ]);
  const costPricesToConvert = useMemo<Array<CostPriceConversion>>(
    () =>
      services.map((service, index) => ({
        serviceId: service.id,
        amount: selectedServiceCostSummaries[index]?.totalCost ?? null,
        currency: selectedServiceCostSummaries[index]?.currency ?? null,
      })),
    [selectedServiceCostSummaries, services],
  );
  const { convertedCostsByService, isLoadingByService } = useGetConvertedCostPricesInAud(costPricesToConvert);
  const selectedSupplierCostPrices = useMemo<Array<SupplierCostPrices>>(() => {
    if (!selectedDepartureKey || !selectedDepartures.length) {
      return [];
    }

    const departureIds = new Set(selectedDepartures.map((departure) => departure.departureId));
    const season = selectedDepartures.map((departure) => seasonByDeparture.get(departure.departureId)).find(Boolean);

    return services.map((service, index) => ({
      serviceId: service.id,
      ...pricesForDeparture(results[index].data, departureIds, season),
    }));
  }, [results, selectedDepartureKey, selectedDepartures, seasonByDeparture, services]);
  const aggregateCurrencies = useMemo(
    () => getAggregateCostPriceCurrencies(selectedSupplierCostPrices),
    [selectedSupplierCostPrices],
  );
  const { audToSourceCurrencyRate, isLoadingByCurrency } = useGetAudExchangeRates(aggregateCurrencies);
  const aggregateCostPrices = useMemo(
    () => aggregateCostPricesInAud(selectedSupplierCostPrices, audToSourceCurrencyRate),
    [audToSourceCurrencyRate, selectedSupplierCostPrices],
  );
  // From the combined inventory, which counts each traveller once however many suppliers carry them.
  const allServicesInventorySummary = useMemo(() => {
    const { units, singles } = summariseDepartureCost(
      ownInventory(inventoryAtDate(inventory, snapshotDate), services[0]?.tourOfferId),
      [],
    );
    return { units, singles };
  }, [inventory, services, snapshotDate]);
  const aggregateInventory = useMemo(
    () =>
      services
        .map((service) => inventoryAtDate(inventoriesByService[service.id], snapshotDate))
        .find((serviceInventory) => serviceInventory !== undefined),
    [inventoriesByService, services, snapshotDate],
  );
  const aggregateAnnotation = useMemo(() => {
    if (!aggregateInventory || !selectedDepartures[0]) {
      return undefined;
    }

    const aggregatePricing =
      aggregateCostPrices?.kind === 'sliding'
        ? aggregateCostPrices.pricing
        : aggregateCostPrices?.kind === 'fixed'
          ? fixedPricingAsScale(aggregateCostPrices.pricing, 1)
          : [];

    return buildScaleAnnotation(
      aggregateInventory,
      selectedDepartures[0].departureName,
      aggregatePricing,
      services[0]?.tourOfferId,
    );
  }, [aggregateCostPrices, aggregateInventory, selectedDepartures, services]);
  const isLoadingAggregate = aggregateCurrencies.some((currency) => isLoadingByCurrency[currency]);

  if (results.some((result) => result.isLoading)) {
    return <CircularProgress />;
  }

  const error = results.find((result) => result.error)?.error;
  if (error) {
    return <Alert severity="error">Could not load all service cost prices: {error.message}</Alert>;
  }

  const toggleSection = (key: string) =>
    setExpandedSections((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  const openSection = (key: string) => {
    setExpandedSections((current) => new Set(current).add(key));
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const uploadService = services.find((service) => service.id === uploadServiceId);

  const renderSupplierSections = (groupKey: string, group: Array<TourFormDeparture>, showCost: boolean) => {
    const departureIds = new Set(group.map((departure) => departure.departureId));
    const season = group.map((departure) => seasonByDeparture.get(departure.departureId)).find(Boolean);
    const keys = services.map((service) => `${groupKey}:${service.id}`);
    const allExpanded = keys.every((key) => expandedSections.has(key));

    return (
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" fontWeight={600}>
            {showCost ? 'Suppliers' : group[0].departureName}
          </Typography>
          <Button
            size="small"
            onClick={() =>
              setExpandedSections((current) => {
                const next = new Set(current);
                keys.forEach((key) => (allExpanded ? next.delete(key) : next.add(key)));
                return next;
              })
            }
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Stack>

        {services.map((service, index) => {
          const key = keys[index];
          const data = results[index].data;
          const pricing = servicePricing(service);
          const { sliding, fixed } = pricesForDeparture(data, departureIds, season);
          const annotation = selectedDepartureKey
            ? buildScaleAnnotation(
                inventoryAtDate(inventoriesByService[service.id], snapshotDate),
                group[0].departureName,
                sliding,
                service.tourOfferId,
              )
            : undefined;
          const summary = selectedServiceCostSummaries[index];
          const canUploadHere = pricing.canUploadSlidingScale && !pricing.isByDeparture;

          return (
            <SupplierCostSection
              key={key}
              ref={(element) => {
                sectionRefs.current[key] = element;
              }}
              service={service}
              expanded={expandedSections.has(key)}
              onToggle={() => toggleSection(key)}
              cost={
                showCost && summary
                  ? {
                      totalCost: summary.totalCost,
                      currency: summary.currency,
                      units: summary.units,
                      convertedCost: convertedCostsByService[service.id],
                      isConverting: isLoadingByService[service.id],
                    }
                  : undefined
              }
              uploadLabel={serviceCostPriceRows(data).pricing.length ? 'Re-upload scale' : 'Upload scale'}
              onUpload={canUploadHere ? () => setUploadServiceId(service.id) : undefined}
              onManagePricing={() => onManageService(service.id)}
            >
              {pricing.kind !== 'Fixed' && pricing.kind !== 'FixedByDeparture' ? (
                <SlidingScaleTable
                  pricing={sliding}
                  annotation={annotation}
                  showSingleSupplement={service.costPriceType !== 'FixedPerGroup'}
                />
              ) : (
                <FixedPriceTable prices={fixed} annotation={annotation} />
              )}
            </SupplierCostSection>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack spacing={3}>
      <TourDepartureSelector
        departures={departures}
        selectedDepartureKey={selectedDepartureKey}
        onSelectDeparture={setSelectedDepartureKey}
      />

      {!!selectedDepartureKey && !!selectedDepartures.length && (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            Cost summary · {selectedDepartures[0].departureName}
          </Typography>
          {isLoadingInventory ? (
            <CircularProgress size={28} />
          ) : (
            <CostBreakdown
              travellers={allServicesInventorySummary}
              suppliers={services.map((service, index) => ({
                key: service.id,
                name: <TourServiceOptionLabel service={service} />,
                summary: selectedServiceCostSummaries[index],
                audCost: convertedCostsByService[service.id],
                isConverting: isLoadingByService[service.id],
                onSelect: () => openSection(`${selectedDepartureKey}:${service.id}`),
              }))}
            />
          )}
        </Stack>
      )}

      {!!selectedDepartureKey && !!selectedDepartures.length && (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            Aggregate cost price (AUD)
          </Typography>
          {isLoadingAggregate ? (
            <CircularProgress size={28} />
          ) : aggregateCostPrices?.kind === 'sliding' ? (
            <SlidingScaleTable
              pricing={aggregateCostPrices.pricing}
              annotation={aggregateAnnotation}
              showSingleSupplement={services.some((service) => service.costPriceType === 'SlidingScale')}
            />
          ) : aggregateCostPrices?.kind === 'fixed' ? (
            <FixedPriceTable prices={aggregateCostPrices.pricing} annotation={aggregateAnnotation} />
          ) : (
            <Alert severity="info">No supplier cost prices are available to aggregate for this departure.</Alert>
          )}
        </Stack>
      )}

      {!!selectedDepartureKey && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>
            Inventory
          </Typography>
          {linkedService ? (
            <SharedInventoryNote />
          ) : (
            <TourInventorySection
              departures={departures}
              selectedDepartureId={selectedDepartureKey}
              inventory={inventory}
              tourOfferId={services[0]?.tourOfferId}
              snapshotDate={snapshotDate}
              onDateChange={onDateChange}
              items={[]}
              showCosts={false}
              showCostChart
              isLoading={isLoadingInventory}
              error={inventoryError}
            />
          )}
        </Stack>
      )}

      {visibleDepartureGroups.map(([groupKey, group]) => (
        <Box key={groupKey}>{renderSupplierSections(groupKey, group, !!selectedDepartureKey)}</Box>
      ))}

      {!!uploadService && (
        <SlidingScaleUploadDialog
          open
          service={uploadService}
          existingRowCount={serviceCostPriceRows(results[services.indexOf(uploadService)]?.data).pricing.length}
          tourId={tourId}
          onClose={() => setUploadServiceId(null)}
        />
      )}
    </Stack>
  );
}
